import { EntityOnboardingResponse } from '@shared/api';

import { createMachine } from 'xstate';

import { CompanyAddDirectorsStep } from './components/EntityOnboardingStepper/AddMembersStep/CompanyAddDirectorsStep';
import { CompanyAddShareholdersStep } from './components/EntityOnboardingStepper/AddMembersStep/CompanyAddShareholdersStep';
import { CorporateTrustAddBeneficiariesStep } from './components/EntityOnboardingStepper/AddMembersStep/CorporateTrustAddBeneficiariesStep';
import { SmsfAddCorporateTrusteesStep } from './components/EntityOnboardingStepper/AddMembersStep/SmsfAddCorporateTrusteesStep';
import { SmsfAddMembersStep } from './components/EntityOnboardingStepper/AddMembersStep/SmsfAddMembersStep';
import { TrustAddBeneficiariesStep } from './components/EntityOnboardingStepper/AddMembersStep/TrustAddBeneficiariesStep';
import { TrustAddDirectorsStep } from './components/EntityOnboardingStepper/AddMembersStep/TrustAddDirectorsStep';
import { TrustAddShareholdersStep } from './components/EntityOnboardingStepper/AddMembersStep/TrustAddShareholdersStep';
import { TrustAddTrusteesStep } from './components/EntityOnboardingStepper/AddMembersStep/TrustAddTrusteesStep';
import { EntityApplicationTypeStep } from './components/EntityOnboardingStepper/EntityTypeStep/EntityApplicationTypeStep';
import { FormSubmittedStep } from './components/EntityOnboardingStepper/FormSubmittedStep/FormSubmittedStep';
import { FormSubmittingStep } from './components/EntityOnboardingStepper/FormSubmittedStep/FormSubmittingStep';
import { EntityApplicationIntroductionStep } from './components/EntityOnboardingStepper/IntroductionStep/EntityApplicationIntroductionStep';
import { CompanySupplyInformationStep } from './components/EntityOnboardingStepper/SupplyInformationStep/CompanySupplyInformationStep';
import { TrustSupplyInformationStep } from './components/EntityOnboardingStepper/SupplyInformationStep/TrustSupplyInformationStep';
import { UploadDocumentsStep } from './components/EntityOnboardingStepper/UploadDocumentsStep/UploadDocumentsStep';
import { EntityOnboardingStepId, EntityOnboardingData } from './types/EntityOnboarding.types';

export interface Context {
  completedSteps: Partial<Record<EntityOnboardingStepId, boolean>>; // Map of IDs of completed steps
  applicationData: Partial<EntityOnboardingData>;
}

export type Event =
  | { type: 'NEXT'; data?: Partial<EntityOnboardingData> }
  | { type: 'CHANGE_TYPE'; data: Partial<EntityOnboardingData> }
  | { type: 'BACK'; data?: Partial<EntityOnboardingData> }
  | { type: 'COMPLETE_STEP'; stepId: string }
  | { type: 'SUBMIT_FAILED' }
  | { type: 'SAVE_AND_EXIT' }
  | { type: 'SKIP' };

export type Services = {
  fetchEntityOnboardingData: {
    data: EntityOnboardingResponse;
  };
  submitEntityApplicationData: {
    data: { success: boolean };
  };
};

export const entityOnboardingMachine = createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QHsB2AjZBDAThAlqlAMQDCA8gLIAKAMgKIAq9A+gMrPUDaADALqJQAB2Sx8AF3xpBIAB6IAzAE4ePAHQAmAIw8ArEt0A2ACw8AHBoDsAGhABPRDrNqtlpZZ7urWw+bMBff1s0TFwCImI2AEEANVYogDkAERZ6AA0ASUZeASQQETFJaTz5BAUzJWM1UwUFHksFYwtDDVsHBC0NQOCMbDxCKDUAYxwwLHEwKKGh5ABXVHFiHJkCiSlUGVK63TVDXQ1GhUtdNsQAWi0FHaajY7MtV0M77pAQvvDBgBtsD7UAMzA4iGAAtyL0wgMkuMsMQIGgwGpCAA3ZAAawRbwhRDU3ywvwBQNB4P6RCh4iwCGRyCG43WOWWeVWRQ2eXaRyUajMXIMpjMli0Bl0xlspUMSi01WMTweFQOxiuL0xJK+PwG-0BILBoWVZJhYBwOGQODUQk+4z+RoAtmolb9cfiNUTtR9dZTUCiacz6fwVqI1sVQKLxZLpVpZYcTvZFAoJVyKkLzPzBcZFcS7arsX9CBA2OScJIiLmwEIlj7GX7mZtHDxymoDLUlEoxcoNBozKcEBp3JzzC0GmZdDHLqnnWr7Wqs6gc3mC1AiyWuFpcsIK+sqx0a8568om0oW22O8odoYLLVtK3dJdLCP3mqAI6zfBDVEZBaG4gJdLZMsrwprkqOLoNbVO4litsYriVO4HYXFc1QDk8uj3I8zxBK8aZqgAVrMlpCIwyDUKuaClsu+RESygaKBoVRKPclh7JYxgeB4CiHieEr8rUuhAfsHgBGhtpYTheEEeRSxLr6f4BnIVE0XRDFMfUNaHg0HJipYDSqLx5g3ligzYbh+GEVJqBLBopFMv+lFlNRdbybojHMcpUZlG4li7BYOg8VYOkCRh2IGSJxn+qZXAKBZ5Hrgotm0Y8DmKSxh4DsehgmGYWk+fxPSjgFwlGWJXDGBFJlRTF9mOUprEucoly7EByV6JlunKmogX5SZSy6MVIWlXJcUVYl1XuBKhhXAcGV8c1vxgJaWD4J8MT6vgWaeusxAAEJRKQADSDK-j1AEdGYJguFoQEaL4Z2DpY7YubB1wIXcDz0ah2W3tiM1zQtS0rbSaBqLAYBTvQs3zaQyAQGAsLwoi7pohi-mDJ982LTgy1Pn9qAA0DEAg194OQ26HqY963WVodxhNi4pgNIx3HlMdHYPGGaiXh42k1MoU1qsj31o79zLY8DoOfATUP6oaxqmuaVo2ojai86j6Orf9gPC-jENgET1Ik-we1kSVFNU1oNNHMY9NcoYTM8BoOyNJeJgtOYlxKNzH0i0rAvrCaOMDGLxD0JQUQZLQLAUEkrAZAk1AAKrfmTVkyQgFTqL4Hg3Mc0U8FbLnM9cYGNF2DwKI7btIx7P0Y4LQi+0Q-uB8Hofh6wABK9BsPQCTx5JB3WZTal1I0TuGI8rQue4VRcj4NZSkBw1lwrFf81X3tIj9dh42DmvQ6gCJUuics5eXX2eyv-1r-zG8i2L2sq6gpM9+T1mXModY1o2xzNnUOftA8wFGGKEwtE+QNBTH5I+i8T6VzvmoC+y0r4a0hsQCWRoTRmnEBaHA1pBLuygcvGBcC-gIK3oTKkd8H7lkNtZFOuwlIZ0HBobO1tWxqDqDwc2TQ2wmyYgvRW0DMawMrpATenwPxfn1pZaSpRXCXhcDbGs3ELpAWikzHQGg1DZ22BBVK6UuwL0tMgdA80wCnzvhtLau0fwG17knQe6hmYXVoiedhHZGEXTfjWM6rgTYCi6OA96gwDFGM+CY-hgsQRgGfFET4ow8R2FPpAWgOtRESMiodfY6hyiKNSibDSt1f4aQUC4YaKgrClLMAofRhjjGmIEREqJMSxgQHiUIiASSaQpIkpQmxpQi7uU8GGLO7DjDGDHr-c26htA6HKFYWe4oqnBNCfggRasICUGqSE-2cJd6wxRAfHBgSNlLOVisnG6zFk3zIbrPgqSqFJ2me5RhA4fAj0bKlJmadWY6EbOYJCvhBwLJqWE72qzznGP9igqW6DMHYPlkEoFyzBagqOZcuG5C9ZWMkRRe5NZHm-JeQKJsP9HAVBGhYPkFQIIWABf4vSah4UhNqeE4EkTUTRNic0hJEAW4zWQBMEij9E7SKQhKQcZ0rAHBaG2MZjgR5VB0ToUZo09CAsZcC-69S2WNLiVynlBj+WLgTlIxAfSNHigqYw4ZoymaU0MC4CwEEjCqFtpU2lLUGXHK9v9GuU4-bb0oOQdaIdWDNxYFHWO3dulPyTm4op7geRAVSkoLsTM4y7HKN4poV1jiqs9WfLGPqPj+wDUGhgYdyARxYG3DuXdbk9NlSw1QNZVCOUbFVApHg6yXk8BU0aRKsroQgR6plq915gs2dvbZe84b7LhUckd58x0os1rfa5dbo29KaByTxjFxSaMqO2kl9E1ANDKalE8FShS5oXVjQhdhx1gAhQaVB0sMGywOfS+d6rb1LouSuq5XoMVGuxb0m2ca3BCkTWKFNucIPVFSm2BsFSji6Gvd+wRy9IAPrEWkSN+0N0mvFP081QyRnWtzua3Yxw9BgXcFyMBb06WA3ELMIQ1BDRZhCZAlG6HVkiK2TDfeCMIHMdY+x5AnGER8MRSCnG-H-1orXZitJ1krg8AlMm7wbZ6xCmFC5aiBwNEJilFyU90UF6ibYxx4x3G+YnKRXJ6+29IVoJllgw+ATsYsasxJmz0n7OyfViQrWAG6RAcFcasoQENOtn5NpwcunXHRQ5FybQSF0oQRrK6xjLVLPick7Zm9PtfV123g3EO5bK3hrjuuoVigKh2vKKU0w4rjp6faAZ9Qqg9xXDcKZmlOXfh5es1x-zXqC21ygPXIOFXQ3Vs7nh6xBGot6GqJ0aKPgpQmxlZ2Mpa2BzuDSuUEwFnARiZG1JpeAXF2X3k0gqduz4YeaY2dnzBWxv5ow-Au7IXFOAZucpu5pR5Tmw8voTSngux7FcbyORyhfBOPFdeN1Q3Xv5b81d8bX2iE-eQc+qFbnYUibRxdwr6G70-dXf92rkWmuNZTtRdT+w2tJfcRUxiTF+RGHpqd7z6PRuY8+4Q-AwiRY4cW1i9cTEjCcjMC1zwqUpRKBh9nWhWl1MmEGbz87vmuPDvQ5q9lTSWmYbackgVUa6sIH2KNOse4mj0SlPUVN7jRr6GOr4GqPCUdqmG7rhE+uZMapZQ0jlJv0aJPN4aiLIHED0V8LsD+e4FENEjOM-YJ75T6CzdM6i2u3s2cD9drGyK-33cEzO4Tnm-cFaL1j0v4KFPE2p4D+t1ubrqHuCYDSexOhITTyS1bCWGidFtjoVw+f+cB6-UHkvZzl1IJc6+mFz3csk-95+xZRWG8TtIX9sLAPgPrgculTkm2e+XltgOJm9ZNCVFGQYSocuFQ++xDXwvM-i-DBD1qsPureUGqt7LajS2wuDKBNDNDHA7ZhidCJ5tgjItA8h7CT6k516faG7aqcqtJ6p8pQzR6W6Rbx6pxJ6DwORXAfKVCaD1BXREq7rI6Da+7r616f5Y6Fp+pIIlrBqVaRzRw1ZAFW5PBPCsx8iJo9qtjQEGCTKUxgTaD2z0QoEb5oEwJsGlYcGBpcFzbtwLY06x4ICjR7C0LNCuBuBtgD4dAOQSitjJoVJMQzJNgKHMFb7k6-qN7l47JCar6o586oEsFC4uG76-bN4H46HrgFzqJ9r8jqZHAnjEodB7jOCNAg6nphgJYOEf5OGz7Y73oL7iz46uZvruYfrv566+EEL+GPpN46wt5H6HSCHuRITUYI4WCtipoOQaJ5KNB6CXBGBpElEZFf7C5YZHLi4hGHREGJ7xqkGp434lycimDyhALAK1C9EIioBYCWiTBThJCGIjH8G07w6Z7sLlDSFMTK65wjIcgyJNBihNBHh+IMFv5ME2Z4gQCjCwCwDmI7SjGqYHGMRHFy4TxuBMwOTqK9juA2xcjOr0GDrV5PFcYvFvEfGfi4bfG2KOqsLmCeBgRbbDKuI2w7BuIg7mzxGXgrEAxzA4BDBgDkB-AADqYwnw4gwInxliNRfc8esu8uRKSuriJszgxcRGfWjYF0ZJsAFJVJNJ9JWAjJzJyJEuKmSc0udq9G6mCujsZxHW1E26mWLWiqF0DGMJL23hG+QgswOABQUMm0XxexuhTEqUnJqp3JYovJtErMGkTE8YikBgZJpp5pogUMcpqJvSo0zgDkcqxcURNg+m6kJ6ts9u4JQo2WhpLU+AEAN6xAcQLcGQAAYhkKQFEIwBkOQAkKkC3C3OQC3EGSakrmtubGlLbL4CMoeC2qzA-g8I6hPAvKmUVpqjermOMHgTaeuCeFUK4FKDGLbLTIOK4t3qwpBF2CoNITbF2WmQbj-n2eSAal0vhgIXLi4IxKNH3lOYep2Mml1rik2ocPMq-oMN2WuayhuQOWZGyUnCOfueOUeShieQcBpJoPHs8hknojeYiKuZkb2d+v2QauFDHuuAcDLi1r4HoCXJevkoRpTKzP3iXB2YxCuT2euRBZuXgUVDBbUXuWOYeZOV+a4soqzCoPUGwuYMmrhbxq9gAOKjBAwZBJA7zTp7JV50p3mZGWbsVgCcVJBU7BFDmHQPD2lNjrYOSFJAnRnRSsK1CdC0TygDiNDMVCVsUcWoBcV46Sz5Er4fqCVf7CX6VcUSVoAUI7mRYyV2pyVwUaS0wakmpPAcg3SWx7jcSIH3HJm-DmVY7C53z9n5gslVmdhXB2oIVaTIVXCoWdh9ggSOryjJoniobAXBV+Gz7hWLCBlSXPz7BeW+DpSuCthhh8jUU2z7kj4jICibY6UDHfqvjiZQCInEBcUsCZk5l5kFlFkllRxVr0AxAZD0C0lRVhgjLwSMQlzZzUQtDtbVkGAnotBHAZqcKjTNUhWtWoDtWdXdW9W5n5mFnFnlo0AMDMBRVwWxUa7xUniJXUUxhzlkZSh7D7DlALyQxMjZnzAQCwBqA-UVi7EvmlDBhcSaY2xSjyhKUdbIZrW2wK7KA9qBBoSoCazwB5AHIkXWQXAwTiF1hxZASqAWB0xZUPGDAjBjATBTAzDzDiC42KlRlsi27cJjmTkQQXSGALzjhEBM29Kn6njQ18luJ7gw5VA1AXTvw1Qjm80ZiDAEiaiIy6gC1UTODC3USi02zi3VRuCy6emJgCjcQGkfp82K3Zj5UDDzhq3JwvULX8gjm8SNAdiZasKpQoZPBXAv6U1qAPhPgvhvjIC22dDHr0T6AAJ8RuDLWnl2rsL1gtCDKTkLxtSiR3KS61E7ZPLHieCVD7CdA1i2y8KC53y23aBOUngQZ1kCgVAdimasKP5Yn8j3By7F14IWWOaIJgBl0nRNh8gJqvK125w2yWGF2jKjJ8gMxt08aZEqFTaay22p51QqAtB7Dmy2wx0RknqJXZzdZXDe6+0fZlG3ZOaQwh3fKszKDlCqlTIWCqLqZ1hI2jJuBJoVLT12a7Wm4iIh1GAcijIHCxYjlPCb197VDOz1DyheKt3AVKGYxl3uJ91V1GA13uXmGMIaIuqLmjIJYDofqwPMqspG46qtLtLSll2MR1iV0D0oNMzh0YMxgOS0R7j25oa6VTgPpiyL3dhcjcT96LW9q0NMRdok21A3SDg+2BVqj4PewYF-7YEAHd0EG6GXACgaIoVPADLOofJdisL521B1DdHLkwOlECJz2cNKPrhhhigaIMP6Amwjy1BJUCjAQqA+AMLlD0QPCsMtWXwcML0WOHQXjqAOzsKeALVXBmFhjHBmp+DX36HQl4MmOCyDFrJHLkPuJnSjLHZhguK5wVChmqRlXpQIYU2SOPHGmSa20xgUPUYaSMUjzQ65wb1-mUz6CabKAWBklH1wMBOqaE21N8RyWNMdaNV1jRT3AQTRQjxdMl2nJBaiz+P2W6HRSdoDP1NuOs47B-GxbSqjJnQzPt2sGTbmNLNRSMQSgNWbieAXEmww5wQSreWDiaI83AXFGXaHO5Xfan2KOnMUwPB2yyG8OjLmAx2M6xVOxZqSrP4HMz0+MR64wiy23mzROMIP4WAgu1Aw46N53mAbbS7sJknSPSQZ19zglumIRpS7paA2oVKZ6NCNgGDpQDZlODBvOb4Ipf6yPG5cqkOfC23RYFMUsO6UzUt5MsxbY8gxjP2NiEtJOBapNl4-NLZW4m0XO+XG3cSHapoP1X1uDgmFyyv9FY5cvEOm44ETC22+AtCJ6ZPuky0u7uTRGT2pQ3RSgJPyxstEsTYlbz1n29OvkjzhF3HqbulWA34GGXhZxNbGHemvNwnT5GufNEJ+N+u-PWRiiUGdCmDqZ7hQFON7h2oeDUSJH6BZJaCGscuf3wsPqL01n0TaBvKUz6Phv2LJT-O1DOLuvE4VM2ZrEbFRBbGGJVOM4npAR1MNYbPnF7iUPlU0yvL1BkkIlwBY1puKl8jqBxaVWqCmCjRMyNAcT-Ke7HFPBduwk9tcZilmkSl0kMlMnDumCjv1CDMNNmFpYXOUx8jmyXD4k+lmkWn8uk3kufzCviiuKyG7DFwmzqY0zZw7X5pVMVCsyIXqUQSmDcRgeIduBhM+Aly2wHBwcwLgV5WEVItHBIeqAodQfof6YMysJMTpVP6Di4Pyw5UwKWWiUGVJC20GASgdtQd7Px1Z1SrtFhkvLIWuzZWgVwvjb5Uh1XAch2PmzVMhnDMmpzztHJpXhciNANAEcCKhWYxtWGgdXLsh0gGciiOCFLV+U1XhEmBWAxhy5716fJN7U8pIgi4ADuZdo+cxMRZ0YjLtNHFga1mF1hdQ8oLno6s+9A+Ocn+gtFJsYq9Ex2r7lG1ETulsfxpTH6wNhQf1U4K7yrkWTEM5tLN0-IUH3W0DvteXEgBXANQNxYFY3Hr7GklhYEjz38JcC8MwRAOA4wsw6C6wRXJLzNh42gVQxw4o4o3EiqSZH6fXHVg3w3aAgNdXEwEArXh4dQ6iGXRTwDKqwFS3A3LGq3qAgNsAqI+AQgNcEASQzXhQ231UCx29EEKgyaRGVgvXaAy3Z3mMgNqZGQ7xswSrY34NZhvWlhP5tGH3pgfigQQAA */
  context: {
    completedSteps: {},
    applicationData: {},
  },
  tsTypes: {} as import('./EntityOnboarding.machine.typegen').Typegen0,
  schema: { context: {} as Context, events: {} as Event, services: {} as Services },
  predictableActionArguments: true,
  id: 'entityOnboarding',
  initial: 'loading',
  states: {
    loading: {
      initial: 'fetchEntityOnboardingData',
      states: {
        fetchEntityOnboardingData: {
          id: 'fetchEntityOnboardingData',
          invoke: {
            src: 'fetchEntityOnboardingData',
            onDone: {
              target: 'findStartingStep',
              actions: ['prefillProfileData'],
            },
            onError: {
              target: 'findStartingStep',
            },
          },
        },
        findStartingStep: {
          always: [
            {
              target: '#jumpToPosition',
            },
          ],
        },
      },
    },
    introduction: {
      id: 'introduction',
      on: {
        NEXT: {
          target: '#entityType',
        },
      },
      meta: {
        step: true,
        Component: EntityApplicationIntroductionStep,
      },
    },
    entityType: {
      id: 'entityType',
      on: {
        NEXT: {
          target: '#handleEntityPath',
          actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
        },
        CHANGE_TYPE: {
          target: '#handleEntityPath',
          actions: ['changeEntityType'],
        },
        BACK: {
          target: '#introduction',
        },
      },
      meta: {
        step: true,
        Component: EntityApplicationTypeStep,
      },
    },
    companyInformation: {
      id: 'companyInformation',
      on: {
        NEXT: {
          target: 'companyAddMembers',
          actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
        },
        BACK: {
          target: '#entityType',
        },
      },
      meta: {
        step: true,
        Component: CompanySupplyInformationStep,
      },
    },
    trustInformation: {
      id: 'trustInformation',
      on: {
        NEXT: {
          target: 'handleAfterTrustInformation',
          actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
        },
        CHANGE_TYPE: {
          actions: ['changeTrustType'],
        },
        BACK: {
          target: '#entityType',
        },
      },
      meta: {
        step: true,
        Component: TrustSupplyInformationStep,
      },
    },
    handleAfterTrustInformation: {
      id: 'handleAfterTrustInformation',
      always: [
        { target: '#corporateTrustAddMembers', cond: 'hasChosenCorporateTrustType' },
        { target: '#individualTrustAddMembers', cond: 'hasChosenIndividualTrustType' },
      ],
    },
    smsfInformation: {
      id: 'smsfInformation',
      always: [{ target: '#smsfAddMembers' }],
    },
    companyAddMembers: {
      id: 'companyAddMembers',
      initial: 'addDirectors',
      meta: {
        step: true,
      },
      states: {
        addDirectors: {
          on: {
            NEXT: {
              target: 'addShareholders',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: '#companyInformation',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: CompanyAddDirectorsStep,
          },
        },
        addShareholders: {
          on: {
            NEXT: {
              target: '#uploadDocuments',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: 'addDirectors',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: CompanyAddShareholdersStep,
          },
        },
      },
    },
    corporateTrustAddMembers: {
      id: 'corporateTrustAddMembers',
      initial: 'addDirectors',
      meta: {
        step: true,
      },
      states: {
        addDirectors: {
          on: {
            NEXT: {
              target: 'addShareholders',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: '#trustInformation',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: TrustAddDirectorsStep,
          },
        },
        addShareholders: {
          on: {
            NEXT: {
              target: 'addBeneficiaries',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: 'addDirectors',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: TrustAddShareholdersStep,
          },
        },
        addBeneficiaries: {
          on: {
            NEXT: {
              target: '#uploadDocuments',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: 'addShareholders',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: CorporateTrustAddBeneficiariesStep,
          },
        },
      },
    },
    individualTrustAddMembers: {
      id: 'individualTrustAddMembers',
      initial: 'addTrustees',
      meta: {
        step: true,
      },
      states: {
        addTrustees: {
          on: {
            NEXT: {
              target: 'addBeneficiaries',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: '#trustInformation',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: TrustAddTrusteesStep,
          },
        },
        addBeneficiaries: {
          on: {
            NEXT: {
              target: '#uploadDocuments',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: 'addTrustees',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: TrustAddBeneficiariesStep,
          },
        },
      },
    },
    smsfAddMembers: {
      id: 'smsfAddMembers',
      initial: 'addIndividuals',
      meta: {
        step: true,
      },
      states: {
        addIndividuals: {
          on: {
            NEXT: {
              target: 'addCorporateMembers',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: '#entityType',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: SmsfAddMembersStep,
          },
        },
        addCorporateMembers: {
          on: {
            NEXT: {
              target: '#uploadDocuments',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
            BACK: {
              target: 'addIndividuals',
              actions: ['saveEntityApplicationFormData', 'assignApplicationData'],
            },
          },
          meta: {
            step: true,
            Component: SmsfAddCorporateTrusteesStep,
          },
        },
      },
    },
    uploadDocuments: {
      id: 'uploadDocuments',
      on: {
        NEXT: {
          target: '#formSubmitted',
        },
        BACK: {
          target: '#backFromUploadDocuments',
        },
      },
      meta: {
        step: true,
        Component: UploadDocumentsStep,
      },
    },
    formSubmittedStep: {
      id: 'formSubmitted',
      initial: 'submitting',
      meta: {
        step: true,
        hidden: true,
      },
      states: {
        submitting: {
          invoke: {
            src: 'submitEntityApplicationData',
            onDone: {
              target: 'submitted',
            },
          },
          on: {
            SUBMIT_FAILED: {
              target: '#uploadDocuments',
            },
          },
          meta: {
            Component: FormSubmittingStep,
          },
        },
        submitted: {
          meta: {
            step: false,
            Component: FormSubmittedStep,
          },
        },
      },
    },
    backFromUploadDocuments: {
      id: 'backFromUploadDocuments',
      always: [
        {
          target: '#companyAddMembers.addShareholders',
          cond: 'hasChosenCompanyType',
        },
        {
          target: '#corporateTrustAddMembers.addBeneficiaries',
          cond: 'hasChosenCorporateTrustType',
        },
        {
          target: '#individualTrustAddMembers.addBeneficiaries',
          cond: 'hasChosenIndividualTrustType',
        },
        {
          target: '#smsfAddMembers.addCorporateMembers',
        },
      ],
    },
    handleEntityPath: {
      id: 'handleEntityPath',
      always: [
        {
          target: '#companyInformation',
          cond: 'hasChosenCompanyType',
        },
        {
          target: '#trustInformation',
          cond: 'hasChosenTrustType',
        },
        {
          target: '#smsfInformation',
        },
      ],
    },
    jumpToPosition: {
      id: 'jumpToPosition',
      always: [
        {
          target: '#introduction',
          cond: 'hasNotCompletedEntityTypeStep',
        },
        {
          target: '#companyJumpToPosition',
          cond: 'hasChosenCompanyType',
        },
        {
          target: '#trustJumpToPosition',
          cond: 'hasChosenTrustType',
        },
        {
          target: '#smsfJumpToPosition',
        },
      ],
    },
    trustJumpToPosition: {
      id: 'trustJumpToPosition',
      always: [
        {
          target: '#individualTrustJumpToPosition',
          cond: 'hasChosenIndividualTrustType',
        },
        {
          target: '#corporateTrustJumpToPosition',
          cond: 'hasChosenCorporateTrustType',
        },
        {
          target: '#trustInformation',
        },
      ],
    },
    individualTrustJumpToPosition: {
      id: 'individualTrustJumpToPosition',
      always: [
        {
          target: '#individualTrustAddMembers.addTrustees',
          cond: 'hasNotCompletedIndividualTrustAddTrusteesStep',
        },
        {
          target: '#individualTrustAddMembers.addBeneficiaries',
          cond: 'hasNotCompletedIndividualTrustAddBeneficiariesStep',
        },
        {
          target: '#uploadDocuments',
        },
      ],
    },
    corporateTrustJumpToPosition: {
      id: 'corporateTrustJumpToPosition',
      always: [
        {
          target: '#corporateTrustAddMembers.addDirectors',
          cond: 'hasNotCompletedCorporateTrustAddDirectorsStep',
        },
        {
          target: '#corporateTrustAddMembers.addShareholders',
          cond: 'hasNotCompletedCorporateTrustAddShareholdersStep',
        },
        {
          target: '#corporateTrustAddMembers.addBeneficiaries',
          cond: 'hasNotCompletedCorporateTrustAddBeneficiariesStep',
        },
        {
          target: '#uploadDocuments',
        },
      ],
    },
    companyJumpToPosition: {
      id: 'companyJumpToPosition',
      always: [
        {
          target: '#companyInformation',
          cond: 'hasNotCompletedCompanyInformationStep',
        },
        {
          target: '#companyAddMembers.addDirectors',
          cond: 'hasNotCompletedCompanyAddDirectorsStep',
        },
        {
          target: '#companyAddMembers.addShareholders',
          cond: 'hasNotCompletedCompanyAddShareholdersStep',
        },
        {
          target: '#uploadDocuments',
        },
      ],
    },
    smsfJumpToPosition: {
      id: 'smsfJumpToPosition',
      always: [
        {
          target: '#smsfAddMembers.addIndividuals',
          cond: 'hasNotCompletedSmsfAddMembersStep',
        },
        {
          target: '#smsfAddMembers.addCorporateMembers',
          cond: 'hasNotCompletedSmsfAddTrusteesStep',
        },
        {
          target: '#uploadDocuments',
        },
      ],
    },
  },
  on: {
    COMPLETE_STEP: {
      actions: ['completeStep'],
    },
    SAVE_AND_EXIT: {
      actions: ['redirectToDashboard'],
    },
  },
});
